// Comprehensive reports panel — used by both Admin and Restaurant dashboards.
// Supports period=day|month|year with charts, top items/drivers/restaurants, comparison
// vs previous period, and CSV / PDF export.
//
// Props:
//   scope: "admin" | "restaurant"  — determines endpoint & shown blocks
//   onClose(): void
//   restaurantName?: string        — displayed in the PDF header (restaurant scope)

import { useCallback, useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Download, FileText, TrendingUp, TrendingDown, Minus, RefreshCw, Calendar, BarChart3, Save } from "lucide-react";

const PERIODS = [
  { key: "day",   label: "يومي",  count: 30, min: 7,  max: 90 },
  { key: "month", label: "شهري",  count: 12, min: 3,  max: 24 },
  { key: "year",  label: "سنوي",  count: 5,  min: 2,  max: 10 },
];

function fmtMoney(n) {
  return (Number(n || 0)).toLocaleString("ar-LY", { maximumFractionDigits: 2 });
}
function fmtPct(v) {
  if (v == null) return "—";
  const s = v > 0 ? "+" : "";
  return `${s}${Number(v).toFixed(1)}%`;
}
function pctIcon(v) {
  if (v == null) return <Minus size={12}/>;
  if (v > 0)     return <TrendingUp size={12}/>;
  if (v < 0)     return <TrendingDown size={12}/>;
  return <Minus size={12}/>;
}
function pctClass(v) {
  if (v == null || v === 0) return "text-[var(--text-secondary)]";
  return v > 0 ? "text-green-600" : "text-red-500";
}

function csvEscape(s) {
  const str = String(s ?? "");
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function download(filename, content, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob(["\uFEFF" + content], { type: mime }); // BOM for Arabic in Excel
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 300);
}

export default function ReportsPanel({ scope = "admin", onClose, restaurantName = "" }) {
  const endpoint = scope === "restaurant" ? "/restaurant/reports" : "/admin/reports";
  const [period, setPeriod] = useState("day");
  const [count, setCount] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Per-restaurant filter (admin scope only)
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
  const selectedRestaurantName = useMemo(
    () => restaurants.find((r) => r.id === selectedRestaurantId)?.name || "",
    [restaurants, selectedRestaurantId]
  );

  useEffect(() => {
    if (scope !== "admin") return;
    (async () => {
      try {
        const { data: d } = await api.get("/restaurants");
        setRestaurants(Array.isArray(d) ? d : (d?.restaurants || []));
      } catch { /* ignore */ }
    })();
  }, [scope]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ period, count: String(count) });
      if (scope === "admin" && selectedRestaurantId) params.set("restaurant_id", selectedRestaurantId);
      const { data: d } = await api.get(`${endpoint}?${params.toString()}`);
      setData(d);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  }, [endpoint, period, count, scope, selectedRestaurantId]);

  // When the period tab changes, reset count to that period's default.
  const switchPeriod = (p) => {
    const cfg = PERIODS.find((x) => x.key === p);
    setPeriod(p);
    setCount(cfg?.count || 30);
  };

  useEffect(() => { load(); }, [load]);

  const cfg = useMemo(() => PERIODS.find((x) => x.key === period), [period]);

  // Simple bar chart values (normalize by max)
  const chartMax = useMemo(() => {
    if (!data?.rows?.length) return 1;
    const m = Math.max(...data.rows.map((r) => r.revenue), 1);
    return m;
  }, [data]);

  // ---------- Export ----------
  const exportCSV = () => {
    if (!data) return;
    const head = [
      cfg.label,
      "عدد الطلبات",
      "المسلّمة",
      "الملغاة",
      "المجموع الفرعي (د.ل)",
      "رسوم التوصيل (د.ل)",
      "الإيرادات (د.ل)",
      "متوسط الطلب (د.ل)",
    ];
    const rows = data.rows.map((r) => [
      r.label,
      r.orders_count, r.delivered_count, r.cancelled_count,
      r.subtotal, r.delivery_fees, r.revenue, r.avg_order,
    ]);
    const totals = data.totals;
    rows.push([
      "الإجمالي",
      totals.orders_count, totals.delivered_count, totals.cancelled_count,
      totals.subtotal, totals.delivery_fees, totals.revenue, totals.avg_order,
    ]);
    const csv = [head, ...rows].map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const scopeLabel = scope === "restaurant" ? (restaurantName || "restaurant") : "admin";
    download(`talabya-report-${scopeLabel}-${period}-${data.from}_${data.to}.csv`, csv);
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = document.getElementById("talabya-report-print");
    if (!doc) return;
    // Open a new window with just the report content styled for A4 (print dialog for Save as PDF via OS)
    const w = window.open("", "_blank");
    if (!w) return;
    const html = `<!doctype html>
<html lang="ar" dir="rtl">
<head><meta charset="utf-8"/>
<title>تقرير طلبية</title>
<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;900&family=Tajawal:wght@400;700&display=swap" rel="stylesheet"/>
<style>
  body { font-family: 'Tajawal','Cairo',sans-serif; color:#111; padding:24px; }
  h1 { font-family:'Cairo',sans-serif; font-weight:900; color:#ff6a1a; margin-bottom:4px; }
  h2 { font-family:'Cairo',sans-serif; font-weight:900; margin-top:24px; border-bottom:2px solid #ff6a1a; padding-bottom:4px; }
  table { width:100%; border-collapse:collapse; margin-top:8px; font-size:12px; }
  th,td { border:1px solid #ddd; padding:6px 8px; text-align:right; }
  th { background:#fff5ef; color:#ff6a1a; font-family:'Cairo',sans-serif; }
  .kpi { display:inline-block; margin-left:12px; margin-bottom:8px; padding:8px 12px; border:1px solid #eee; border-radius:8px; }
  .kpi .l { font-size:11px; color:#666; }
  .kpi .v { font-family:'Cairo',sans-serif; font-weight:900; font-size:16px; }
  @page { size:A4; margin:12mm; }
</style></head>
<body>${doc.innerHTML}
<script>window.onload=function(){setTimeout(function(){window.print();},200);};</script>
</body></html>`;
    w.document.write(html);
    w.document.close();
  };

  // Save PDF directly to disk (no print dialog). Uses html2pdf.js under the hood.
  const savePDF = async () => {
    if (!data) return;
    const src = document.getElementById("talabya-report-print");
    if (!src) return;
    // Build a full-page clone with print-friendly styling so the PDF is nicely formatted.
    const wrap = document.createElement("div");
    wrap.style.fontFamily = "'Tajawal','Cairo',sans-serif";
    wrap.style.direction = "rtl";
    wrap.style.padding = "20px";
    wrap.style.color = "#111";
    wrap.style.background = "#fff";
    wrap.style.width = "800px";
    wrap.innerHTML = `
      <style>
        h1{font-family:'Cairo',sans-serif;font-weight:900;color:#ff6a1a;margin:0 0 4px 0;}
        h2{font-family:'Cairo',sans-serif;font-weight:900;margin:16px 0 6px 0;border-bottom:2px solid #ff6a1a;padding-bottom:4px;}
        table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12px;}
        th,td{border:1px solid #ddd;padding:6px 8px;text-align:right;}
        th{background:#fff5ef;color:#ff6a1a;font-family:'Cairo',sans-serif;}
        .talabya-header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #ff6a1a;padding-bottom:10px;margin-bottom:14px;}
        .brand{font-family:'Cairo',sans-serif;font-weight:900;font-size:26px;color:#ff6a1a;}
        .sub{font-size:12px;color:#666;}
      </style>
      <div class="talabya-header">
        <div>
          <div class="brand">طلبية</div>
          <div class="sub">Talabya · تقرير مفصّل</div>
        </div>
        <div style="text-align:left">
          <div class="sub">تاريخ التقرير:</div>
          <div style="font-family:'Cairo',sans-serif;font-weight:900">${new Date().toLocaleString("ar-LY")}</div>
        </div>
      </div>
      ${src.innerHTML}
    `;
    // Hide interactive chart bars gradient issues by re-rendering them inline (they already are).
    document.body.appendChild(wrap);
    try {
      const html2pdfMod = (await import("html2pdf.js")).default;
      const scopeLabel = scope === "restaurant" ? (restaurantName || "restaurant") : "admin";
      const fname = `talabya-report-${scopeLabel}-${period}-${data.from}_${data.to}.pdf`
        .replace(/\s+/g, "-");
      await html2pdfMod()
        .set({
          margin: [10, 10, 10, 10],
          filename: fname,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(wrap)
        .save();
    } catch (e) {
      setErr("تعذّر إنشاء ملف PDF — " + (e?.message || ""));
    } finally {
      try { document.body.removeChild(wrap); } catch { /* noop */ }
    }
  };

  if (!data && loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="reports-loading">
        <div className="bg-white rounded-2xl px-6 py-4">جارِ التحميل…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto" data-testid={`reports-modal-${scope}`}>
      <div className="bg-white w-full max-w-6xl rounded-2xl talabya-shadow-soft overflow-hidden my-6" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-l from-primary-brand to-blue-500 text-white">
          <div className="flex items-center gap-3">
            <BarChart3 size={20}/>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>التقارير</div>
              <div className="text-xs opacity-90">
                {scope === "restaurant" ? "تقارير المطعم" : (selectedRestaurantName ? `تقرير: ${selectedRestaurantName}` : "تقارير النظام")}
                {data && ` · ${data.from} → ${data.to}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} disabled={loading} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-50" title="تحديث" data-testid="reports-refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""}/>
            </button>
            <button onClick={exportCSV} disabled={!data} className="px-3 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center gap-1 text-xs font-bold disabled:opacity-50" data-testid="reports-export-csv">
              <Download size={14}/> CSV
            </button>
            <button onClick={savePDF} disabled={!data} className="px-3 h-9 rounded-lg bg-white text-primary-brand hover:bg-blue-50 flex items-center gap-1 text-xs font-black disabled:opacity-50" data-testid="reports-save-pdf" title="تنزيل تقرير PDF مباشرةً">
              <Save size={14}/> حفظ PDF
            </button>
            <button onClick={exportPDF} disabled={!data} className="px-3 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center gap-1 text-xs font-bold disabled:opacity-50" data-testid="reports-export-pdf" title="فتح نافذة الطباعة">
              <FileText size={14}/> طباعة
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="reports-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Period tabs */}
        <div className="px-5 pt-4 flex flex-wrap items-center gap-2 border-b border-[var(--border)] pb-3">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => switchPeriod(p.key)}
              className={`px-4 py-2 rounded-xl text-sm font-black flex items-center gap-1 transition ${period === p.key ? "bg-primary-brand text-white shadow" : "bg-[var(--surface-alt)] text-[var(--text-primary)] hover:bg-[var(--border)]"}`}
              data-testid={`reports-period-${p.key}`}>
              <Calendar size={13}/> {p.label}
            </button>
          ))}
          {cfg && (
            <div className="mr-auto flex items-center gap-2 text-xs text-[var(--text-secondary)]">
              <span>عدد الفترات:</span>
              <input type="number" min={cfg.min} max={cfg.max} value={count}
                onChange={(e) => setCount(Math.max(cfg.min, Math.min(cfg.max, Number(e.target.value) || cfg.count)))}
                className="w-20 px-2 py-1 rounded-lg border border-[var(--border)] text-center text-sm font-bold"
                data-testid="reports-count-input"/>
              <span>({cfg.min}-{cfg.max})</span>
            </div>
          )}
        </div>

        {/* Per-restaurant filter (admin only) */}
        {scope === "admin" && restaurants.length > 0 && (
          <div className="px-5 pt-3 pb-1 flex flex-wrap items-center gap-2 border-b border-[var(--border)]">
            <span className="text-xs font-bold text-[var(--text-secondary)]">نطاق التقرير:</span>
            <button
              onClick={() => setSelectedRestaurantId("")}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${!selectedRestaurantId ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] hover:bg-[var(--border)]"}`}
              data-testid="reports-restaurant-all">
              كل المطاعم
            </button>
            <select
              value={selectedRestaurantId}
              onChange={(e) => setSelectedRestaurantId(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-bold bg-white focus:border-primary-brand focus:outline-none"
              data-testid="reports-restaurant-select">
              <option value="">— اختر مطعماً —</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {selectedRestaurantId && (
              <span className="text-[10px] font-bold text-primary-brand bg-primary-light px-2 py-1 rounded" data-testid="reports-restaurant-badge">
                {selectedRestaurantName}
              </span>
            )}
          </div>
        )}

        {err && <div className="mx-5 mt-3 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg px-3 py-2" data-testid="reports-error">{err}</div>}

        {/* Report body (also captured for PDF) */}
        <div id="talabya-report-print" className="p-5 space-y-5">
          <div>
            <h1 className="text-2xl font-black text-primary-brand" style={{fontFamily:"Cairo"}}>تقرير طلبية</h1>
            <div className="text-sm text-[var(--text-secondary)]">
              {scope === "restaurant"
                ? `المطعم: ${restaurantName || "—"}`
                : (selectedRestaurantName ? `المطعم: ${selectedRestaurantName}` : "نطاق: كل المطاعم")}
              {" · "}الفترة: <b>{cfg?.label}</b> · من <b>{data?.from}</b> إلى <b>{data?.to}</b>
            </div>
          </div>

          {/* KPIs */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="عدد الطلبات" value={data.totals.orders_count} pct={data.comparison_pct.orders_count} testid="kpi-orders"/>
              <KPI label="المسلّمة" value={data.totals.delivered_count} pct={data.comparison_pct.delivered_count} testid="kpi-delivered"/>
              <KPI label="الإيرادات (د.ل)" value={fmtMoney(data.totals.revenue)} pct={data.comparison_pct.revenue} testid="kpi-revenue"/>
              <KPI label="متوسط الطلب (د.ل)" value={fmtMoney(data.totals.avg_order)} pct={data.comparison_pct.avg_order} testid="kpi-avg"/>
            </div>
          )}

          {/* Bar chart */}
          {data?.rows?.length > 0 && (
            <div className="bg-[var(--surface-alt)] rounded-xl p-4">
              <h2 className="font-black mb-3" style={{fontFamily:"Cairo"}}>الإيرادات لكل فترة</h2>
              <div className="flex items-end gap-2 h-40" data-testid="reports-chart">
                {data.rows.map((r) => {
                  const h = Math.max(2, Math.round((r.revenue / chartMax) * 100));
                  return (
                    <div key={r.key} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${r.label}: ${fmtMoney(r.revenue)} د.ل`}>
                      <div className="text-[10px] font-bold text-[var(--text-secondary)]">{fmtMoney(r.revenue)}</div>
                      <div className="w-full bg-primary-brand rounded-t-md transition-all"
                        style={{height: `${h}%`}} data-testid={`reports-bar-${r.key}`}/>
                      <div className="text-[10px] text-[var(--text-secondary)] truncate w-full text-center" dir="ltr">{r.label.slice(-7)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table */}
          {data?.rows && (
            <div>
              <h2 className="font-black mb-2" style={{fontFamily:"Cairo"}}>التفاصيل ({cfg.label})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-primary-light text-primary-brand">
                    <tr>
                      <th className="px-3 py-2 text-right font-black">{cfg.label}</th>
                      <th className="px-3 py-2 text-right font-black">الطلبات</th>
                      <th className="px-3 py-2 text-right font-black">مسلّمة</th>
                      <th className="px-3 py-2 text-right font-black">ملغاة</th>
                      <th className="px-3 py-2 text-right font-black">المجموع الفرعي</th>
                      <th className="px-3 py-2 text-right font-black">التوصيل</th>
                      <th className="px-3 py-2 text-right font-black">الإيرادات</th>
                      <th className="px-3 py-2 text-right font-black">متوسط</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.key} className="border-b border-[var(--border)] hover:bg-[var(--surface-alt)]" data-testid={`reports-row-${r.key}`}>
                        <td className="px-3 py-2 font-bold" dir="ltr">{r.label}</td>
                        <td className="px-3 py-2">{r.orders_count}</td>
                        <td className="px-3 py-2 text-green-600">{r.delivered_count}</td>
                        <td className="px-3 py-2 text-red-500">{r.cancelled_count}</td>
                        <td className="px-3 py-2">{fmtMoney(r.subtotal)}</td>
                        <td className="px-3 py-2">{fmtMoney(r.delivery_fees)}</td>
                        <td className="px-3 py-2 font-bold">{fmtMoney(r.revenue)}</td>
                        <td className="px-3 py-2">{fmtMoney(r.avg_order)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[var(--surface-alt)] font-black">
                    <tr>
                      <td className="px-3 py-2" style={{fontFamily:"Cairo"}}>الإجمالي</td>
                      <td className="px-3 py-2">{data.totals.orders_count}</td>
                      <td className="px-3 py-2 text-green-600">{data.totals.delivered_count}</td>
                      <td className="px-3 py-2 text-red-500">{data.totals.cancelled_count}</td>
                      <td className="px-3 py-2">{fmtMoney(data.totals.subtotal)}</td>
                      <td className="px-3 py-2">{fmtMoney(data.totals.delivery_fees)}</td>
                      <td className="px-3 py-2">{fmtMoney(data.totals.revenue)}</td>
                      <td className="px-3 py-2">{fmtMoney(data.totals.avg_order)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Payment split + Top items */}
          {data && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[var(--surface-alt)] rounded-xl p-4">
                <h2 className="font-black mb-2" style={{fontFamily:"Cairo"}}>طرق الدفع</h2>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 float-right" style={{
                      width: `${data.totals.orders_count ? (data.payment_split.cash / data.totals.orders_count) * 100 : 0}%`
                    }}/>
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-2">
                  <span>نقد: <b>{data.payment_split.cash}</b></span>
                  <span>إلكتروني: <b>{data.payment_split.electronic}</b></span>
                </div>
              </div>

              <div className="bg-[var(--surface-alt)] rounded-xl p-4">
                <h2 className="font-black mb-2" style={{fontFamily:"Cairo"}}>أفضل 5 أصناف</h2>
                {data.top_items?.length ? (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.top_items.map((it, i) => (
                        <tr key={i} className="border-b border-white last:border-0">
                          <td className="py-1 font-bold">{i + 1}. {it.name}</td>
                          <td className="py-1 text-[var(--text-secondary)]">{it.qty} مرة</td>
                          <td className="py-1 text-primary-brand font-black text-left" dir="ltr">{fmtMoney(it.revenue)} د.ل</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-xs text-[var(--text-secondary)]">لا توجد بيانات</div>}
              </div>
            </div>
          )}

          {/* Top restaurants (admin) + Top drivers (admin) */}
          {data && scope === "admin" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[var(--surface-alt)] rounded-xl p-4">
                <h2 className="font-black mb-2" style={{fontFamily:"Cairo"}}>أفضل 5 مطاعم</h2>
                {data.top_restaurants?.length ? (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.top_restaurants.map((r, i) => (
                        <tr key={r.restaurant_id} className="border-b border-white last:border-0">
                          <td className="py-1 font-bold">{i + 1}. {r.name || "—"}</td>
                          <td className="py-1 text-[var(--text-secondary)]">{r.orders} طلب</td>
                          <td className="py-1 text-primary-brand font-black text-left" dir="ltr">{fmtMoney(r.revenue)} د.ل</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-xs text-[var(--text-secondary)]">لا توجد بيانات</div>}
              </div>
              <div className="bg-[var(--surface-alt)] rounded-xl p-4">
                <h2 className="font-black mb-2" style={{fontFamily:"Cairo"}}>أفضل 5 سائقين</h2>
                {data.top_drivers?.length ? (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.top_drivers.map((d, i) => (
                        <tr key={d.driver_id} className="border-b border-white last:border-0">
                          <td className="py-1 font-bold">{i + 1}. {d.name || "—"}</td>
                          <td className="py-1 text-[var(--text-secondary)]">{d.deliveries} توصيلة</td>
                          <td className="py-1 text-primary-brand font-black text-left" dir="ltr">{fmtMoney(d.earnings)} د.ل</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-xs text-[var(--text-secondary)]">لا توجد بيانات</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, pct, testid }) {
  return (
    <div className="bg-white border border-[var(--border)] rounded-xl p-3" data-testid={testid}>
      <div className="text-xs text-[var(--text-secondary)]">{label}</div>
      <div className="text-2xl font-black mt-0.5" style={{fontFamily:"Cairo"}}>{value}</div>
      <div className={`text-[11px] mt-0.5 font-bold flex items-center gap-1 ${pctClass(pct)}`}>
        {pctIcon(pct)} {fmtPct(pct)} <span className="text-[var(--text-secondary)] font-normal">مقابل الفترة السابقة</span>
      </div>
    </div>
  );
}
